
import { Context } from '@nuxt/types'
import { defineComponent } from 'vue'

// Import State & Types
import identityProvidersForEmailQuery from '~/apollo/queries/auth/identityProvidersForEmail.gql'

import AuthProviderButtons from '~/components/Auth/AuthProviderButtons.vue'
import AuthMixin from '~/mixins/authMixin'
import MetaMixin from '~/mixins/metaMixin'

import { routerVcsMap } from '~/plugins/helpers/provider'
import { AuthActionTypes } from '~/store/account/auth'

import type { FetchResult } from 'apollo-link'
import { AuthUrl, IdentityProvidersForEmailQuery } from '~/types/types'
import { resolveNodes } from '~/utils/array'
import { getDefaultAvatar } from '~/utils/ui'

enum SsoStep {
  BASE_LOGIN = 'BASE_LOGIN',
  EMAIL_INPUT = 'EMAIL_INPUT',
  LOADING = 'LOADING',
  IDP_SELECTION = 'IDP_SELECTION'
}

export default defineComponent({
  name: 'SignIn',
  components: {
    AuthProviderButtons
  },
  middleware: [
    'redirectToHome',
    async function ({
      redirect,
      route,
      $config,
      $providerMetaMap,
      store
    }: Context): Promise<void> {
      const { provider } = route.query
      await store.dispatch(`account/auth/${AuthActionTypes.FETCH_AUTH_URLS}`)
      const authUrls = store.state.account.auth.authUrls as Array<AuthUrl>

      const validProviders = [
        ...($config.githubEnabled ? [routerVcsMap.gh] : []),
        ...($config.githubServerEnabled ? [routerVcsMap.ghe] : []),
        ...($config.gitlabEnabled ? [routerVcsMap.gl] : []),
        ...($config.bitbucketEnabled ? [routerVcsMap.bb] : []),
        ...($config.adsEnabled ? [routerVcsMap.ads] : []),
        ...($config.bitbucketDatacenterEnabled ? [routerVcsMap.bbd] : [])
      ]

      if (
        provider &&
        typeof provider === 'string' &&
        validProviders.includes(provider as routerVcsMap)
      ) {
        // Convert provider value from query param to a valid `VCSProviderChoices` entry
        const vcsProviderChoice = $providerMetaMap[provider].value

        const authUrl = authUrls.find((authUrl) => authUrl.provider === vcsProviderChoice)?.url

        // 307 is temporary redirect
        authUrl && redirect(307, authUrl)
      }
    }
  ],
  mixins: [AuthMixin, MetaMixin],
  data() {
    return {
      metaTitle: 'Login - DeepSource',
      setCanonical: true,
      currentStep: SsoStep.BASE_LOGIN,
      ssoLoginError: false,
      ssoLoginErrorMessage: '',
      ssoEmail: '',
      identityProviders: [] as Array<{
        id: string
        teamName: string
        teamLogin: string
        teamLogo?: string | null
        loginUrl: string
      }>,
      SsoStep
    }
  },
  computed: {
    ssoTitle(): string {
      if (this.currentStep === SsoStep.IDP_SELECTION) {
        return 'Select a team'
      }
      if (this.currentStep !== SsoStep.BASE_LOGIN) {
        return "What's your email address?"
      }
      return 'Log in to DeepSource'
    }
  },
  watch: {
    currentStep(newStep: SsoStep) {
      // Auto-focus email input when entering EMAIL_INPUT step
      if (newStep === SsoStep.EMAIL_INPUT) {
        this.$nextTick(() => {
          const inputEl = (this.$refs.emailInput as Vue)?.$refs?.input as HTMLInputElement
          inputEl?.focus()
        })
      }
    }
  },
  methods: {
    getDefaultAvatar,

    /**
     * Handle email submission with HTML5 validation
     * Uses the input element's built-in validity API via ref
     */
    handleEmailSubmit() {
      if (this.currentStep !== SsoStep.EMAIL_INPUT) return

      const inputEl = (this.$refs.emailInput as Vue)?.$refs?.input as HTMLInputElement
      if (!inputEl) return

      // Trim the email value
      const email = (this.ssoEmail || '').trim()
      this.ssoEmail = email

      // Use HTML5 validity API for validation (handles both empty and format)
      if (!inputEl.checkValidity()) {
        this.ssoLoginError = true
        this.ssoLoginErrorMessage = email
          ? 'Please enter a valid email address.'
          : 'Please enter your email address.'
        return
      }

      // Clear any previous validation errors
      this.ssoLoginError = false
      this.ssoLoginErrorMessage = ''

      // Proceed with SSO verification
      this.verifySsoEmail()
    },

    /**
     * Reset input data and UI state from login via SAML to normal login
     */
    resetSsoFlow() {
      this.currentStep = SsoStep.BASE_LOGIN
      this.ssoLoginError = false
      this.ssoLoginErrorMessage = ''
      this.ssoEmail = ''
      this.identityProviders = []
    },

    /**
     * Handle back button click with progressive navigation
     * - From IDP_SELECTION: go back to EMAIL_INPUT
     * - From other steps: go back to BASE_LOGIN
     */
    handleBackClick() {
      if (this.currentStep === SsoStep.IDP_SELECTION) {
        this.currentStep = SsoStep.EMAIL_INPUT
        this.identityProviders = []
        return
      }
      this.resetSsoFlow()
    },

    /**
     * Perform next action on "Continue with SSO" button click
     *
     * - If the UI is in BASE_LOGIN, switch to EMAIL_INPUT
     * - If the UI is in EMAIL_INPUT with email, verify the email
     */
    nextSsoAction() {
      if (this.currentStep === SsoStep.BASE_LOGIN) {
        this.currentStep = SsoStep.EMAIL_INPUT
      }
    },

    /**
     * Verify user's email for SAML login. If valid, redirect user to the SAML login endpoint.
     */
    async verifySsoEmail() {
      this.currentStep = SsoStep.LOADING
      this.ssoLoginError = false
      this.ssoLoginErrorMessage = ''
      try {
        // Use new query to resolve identity providers for the submitted email
        const idpResponse: FetchResult<IdentityProvidersForEmailQuery> =
          await this.$fetchGraphqlData(
            identityProvidersForEmailQuery,
            { email: this.ssoEmail },
            true
          )

        const edges = idpResponse.data?.identityProvidersForEmail?.edges || []

        if (edges.length === 0) {
          // No IDPs found for the domain
          this.ssoLoginError = true
          this.ssoLoginErrorMessage = 'SSO is not set up for your domain.'
          this.currentStep = SsoStep.EMAIL_INPUT
          return
        }

        const providers = resolveNodes(idpResponse.data?.identityProvidersForEmail)

        if (providers.length === 1) {
          // Single provider: redirect directly
          location.href = providers[0].loginUrl
          return
        }

        // Multiple providers: show selection UI
        this.identityProviders = providers
        this.currentStep = SsoStep.IDP_SELECTION
      } catch (e) {
        const loginError = e as Error
        this.$logErrorAndToast(
          loginError,
          loginError.message.replace('GraphQL error: ', '') as `${string}.`
        )
        this.currentStep = SsoStep.EMAIL_INPUT
      }
    },

    redirectToIdp(loginUrl: string) {
      // Ensure we have a URL and then redirect the browser
      if (!loginUrl) return
      location.href = loginUrl
    }
  }
})
